import { Sha256 } from '@aws-crypto/sha256-js'
import { SignatureV4 } from '@aws-sdk/signature-v4'
import { HttpRequest } from '@smithy/protocol-http'
import { fetchAuthSession } from 'aws-amplify/auth'

import HttpServiceBase, {
  getImpersonationHeaders,
  HttpQueryValue,
  HttpRequestBase,
  HttpServiceBaseResponse,
} from '~/services/http/models'

function appendQueryParams(
  searchParams: URLSearchParams,
  query: Record<string, HttpQueryValue>,
): void {
  Object.entries(query).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, v))
    } else {
      searchParams.append(key, value)
    }
  })
}

class SigV4HttpService implements HttpServiceBase {
  private baseUrl: string

  private name: string

  private region: string = 'us-east-1'

  constructor(name: string, baseUrl: string) {
    this.name = name
    this.baseUrl = baseUrl

    if (!this.baseUrl) {
      throw new Error(
        `Base URL for ${this.name} is missing. Did you forget to set the environment variable?`,
      )
    }
  }

  private async signedFetch<T>({
    headers,
    method,
    path,
    payload,
    query,
  }: HttpRequestBase & { method: string }): Promise<
    HttpServiceBaseResponse<T>
  > {
    const { credentials } = await fetchAuthSession()

    if (!credentials) {
      throw new Error('No credentials available from Cognito Identity Pool')
    }
    const url = new URL(path, this.baseUrl)
    if (query) {
      appendQueryParams(url.searchParams, query)
    }
    const body = payload ? JSON.stringify(payload) : undefined
    const impersonationHeaders = getImpersonationHeaders()
    const request = new HttpRequest({
      method,
      protocol: url.protocol,
      hostname: url.hostname,
      path: url.pathname + url.search,
      headers: {
        'Content-Type': 'application/json',
        host: url.hostname,
        ...impersonationHeaders,
        ...headers,
      },
      ...(body && { body }),
    })
    const signer = new SignatureV4({
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken,
      },
      region: this.region,
      service: 'execute-api',
      sha256: Sha256,
    })
    const signedRequest = await signer.sign(request)
    const response = await fetch(url.toString(), {
      method,
      headers: signedRequest.headers as Record<string, string>,
      body,
    })
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
    const data = await response.json()
    return { data }
  }

  public get<T>(request: HttpRequestBase) {
    return this.signedFetch<T>({ ...request, method: 'GET' })
  }

  public post<T>(request: HttpRequestBase) {
    return this.signedFetch<T>({ ...request, method: 'POST' })
  }

  public put<T>(request: HttpRequestBase) {
    return this.signedFetch<T>({ ...request, method: 'PUT' })
  }

  public patch<T>(request: HttpRequestBase) {
    return this.signedFetch<T>({ ...request, method: 'PATCH' })
  }

  public delete<T>(request: HttpRequestBase) {
    return this.signedFetch<T>({ ...request, method: 'DELETE' })
  }
}
export default SigV4HttpService
